import React, { useState, useRef, useEffect } from 'react';
import { Send, Database, FileText, Loader2, X, AlertCircle, MessageSquare } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { Message, QueryResult, DataProcessingResponse } from './types';
import { ResultViewer } from './ResultViewer';
import { ProcessedResultViewer } from './ProcessedResultViewer';
import { claudeQueryService } from '../../../../services/claudeQueryService';

interface ChatInterfaceProps {
    onAddResult: (result: QueryResult) => void;
    selectedResults: QueryResult[];
    hasSelectedResults: boolean;
    onClearSelection: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
    onAddResult, 
    selectedResults,
    hasSelectedResults,
    onClearSelection
}) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewingResult, setViewingResult] = useState<QueryResult | null>(null);
    const [viewingProcessedResult, setViewingProcessedResult] = useState<DataProcessingResponse | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [inputValue]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!inputValue.trim() || isLoading || !user?.companyNo) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: inputValue.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        try {
            if (hasSelectedResults) {
                // Processing mode - process selected datasets
                const datasets = selectedResults.map(result => ({
                    queryId: result.queryId,
                    originalQuery: result.query,
                    rows: result.rows.slice(0, 100) // Limit to 100 rows per dataset
                }));

                const processedData = await claudeQueryService.processData({
                    query: userMessage.content,
                    companyNo: user.companyNo,
                    selectedDatasets: datasets
                });

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: processedData.success
                        ? processedData.explanation || 'Data prosessert'
                        : processedData.errorMessage || 'Prosessering feilet',
                    timestamp: new Date(),
                    processedResult: processedData
                };

                setMessages(prev => [...prev, assistantMessage]);
            } else {
                // Database mode - execute query
                const data = await claudeQueryService.executeQuery({
                    query: userMessage.content,
                    companyNo: user.companyNo,
                    maxRows: 100
                });

                const result: QueryResult = {
                    queryId: data.queryId,
                    query: userMessage.content,
                    timestamp: data.timestamp,
                    success: data.success,
                    generatedSQL: data.generatedSQL,
                    explanation: data.explanation,
                    executionTimeMs: data.executionTimeMs,
                    rowCount: data.rowCount,
                    tokensUsed: data.tokensUsed,
                    columns: data.columns || [],
                    rows: data.rows || [],
                    presentation: data.presentation,
                    errorMessage: data.errorMessage,
                    errorType: data.errorType
                };

                onAddResult(result);

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: data.success 
                        ? data.explanation || `Spørringen returnerte ${data.rowCount} rad${data.rowCount !== 1 ? 'er' : ''}.`
                        : data.errorMessage || 'Spørringen feilet',
                    timestamp: new Date(),
                    result
                };

                setMessages(prev => [...prev, assistantMessage]);
            }

        } catch (error) {
            console.error('Error executing query:', error);
            
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: error instanceof Error ? error.message : 'Det oppstod en feil ved kommunikasjon med serveren.',
                timestamp: new Date()
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const mode = hasSelectedResults ? 'processing' : 'database';

    return (
        <>
            <div className="bg-white rounded-lg shadow flex flex-col h-full">
                {/* Mode Indicator */}
                <div className={`px-4 py-3 border-b ${
                    mode === 'database' 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-purple-50 border-purple-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {mode === 'database' ? (
                                <>
                                    <Database size={18} className="text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">
                                        Database Spørring Modus
                                    </span>
                                </>
                            ) : (
                                <>
                                    <FileText size={18} className="text-purple-600" />
                                    <span className="text-sm font-medium text-purple-900">
                                        Data Prosessering ({selectedResults.length} resultat{selectedResults.length !== 1 ? 'er' : ''} valgt)
                                    </span>
                                </>
                            )}
                        </div>
                        {hasSelectedResults && (
                            <button
                                onClick={onClearSelection}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded transition-colors"
                            >
                                <X size={14} />
                                Fjern valg
                            </button>
                        )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                        {mode === 'database' 
                            ? 'Still spørsmål om data i lagerdatabasen' 
                            : 'Analyser eller visualiser valgte resultater'}
                    </p>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-medium mb-2">Ingen meldinger ennå</p>
                            <p className="text-sm">Still et spørsmål for å komme i gang</p>
                        </div>
                    )}

                    {messages.map(message => (
                        <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-3xl rounded-lg p-4 ${
                                message.role === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-800'
                            }`}>
                                <div className="flex items-start gap-3">
                                    <div className="flex-1">
                                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                        
                                        {message.result && message.result.success && (
                                            <div className="mt-3 pt-3 border-t border-gray-300">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                                        <Database size={14} />
                                                        <span>{message.result.rowCount} rader</span>
                                                        <span>•</span>
                                                        <span>{message.result.executionTimeMs}ms</span>
                                                        {message.result.tokensUsed && (
                                                            <>
                                                                <span>•</span>
                                                                <span>{message.result.tokensUsed} tokens</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setViewingResult(message.result!)}
                                                    className="w-full px-3 py-2 bg-white text-blue-600 rounded text-sm font-medium hover:bg-blue-50 transition-colors"
                                                >
                                                    Se resultat
                                                </button>
                                            </div>
                                        )}

                                        {message.result && !message.result.success && (
                                            <div className="mt-3 pt-3 border-t border-red-300">
                                                <div className="flex items-start gap-2 text-sm text-red-700">
                                                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="font-medium">Feil</p>
                                                        <p className="text-xs">{message.result.errorMessage}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {message.processedResult && message.processedResult.success && (
                                            <div className="mt-3 pt-3 border-t border-gray-300">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                                        <FileText size={14} />
                                                        <span>{message.processedResult.outputType}</span>
                                                        <span>•</span>
                                                        <span>{message.processedResult.executionTimeMs}ms</span>
                                                        {message.processedResult.tokensUsed && (
                                                            <>
                                                                <span>•</span>
                                                                <span>{message.processedResult.tokensUsed} tokens</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setViewingProcessedResult(message.processedResult!)}
                                                    className="w-full px-3 py-2 bg-white text-purple-600 rounded text-sm font-medium hover:bg-purple-50 transition-colors"
                                                >
                                                    Se resultat
                                                </button>
                                            </div>
                                        )}

                                        {message.processedResult && !message.processedResult.success && (
                                            <div className="mt-3 pt-3 border-t border-red-300">
                                                <div className="flex items-start gap-2 text-sm text-red-700">
                                                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="font-medium">Feil</p>
                                                        <p className="text-xs">{message.processedResult.errorMessage}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-3">
                                <Loader2 size={20} className="animate-spin text-blue-600" />
                                <span className="text-sm text-gray-600">Behandler spørring...</span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t p-4 bg-gray-50">
                    <form onSubmit={handleSubmit} className="flex gap-2">
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                            }}
                            placeholder={mode === 'database' 
                                ? 'Still et spørsmål om lagerdatabasen...' 
                                : 'Hva vil du gjøre med de valgte resultatene?'}
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[50px] max-h-32"
                            rows={1}
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            disabled={!inputValue.trim() || isLoading}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isLoading ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <Send size={20} />
                            )}
                        </button>
                    </form>
                    <p className="text-xs text-gray-500 mt-2">
                        Trykk Enter for å sende, Shift+Enter for ny linje
                    </p>
                </div>
            </div>

            {/* Result Viewer Modal */}
            {viewingResult && (
                <ResultViewer 
                    result={viewingResult}
                    onClose={() => setViewingResult(null)}
                />
            )}

            {/* Processed Result Viewer Modal */}
            {viewingProcessedResult && (
                <ProcessedResultViewer 
                    result={viewingProcessedResult}
                    onClose={() => setViewingProcessedResult(null)}
                />
            )}
        </>
    );
};
